(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-da-gemstone-picker]';
  const SEARCH_VIEW = 'da-design-group';
  const CACHE_VERSION = 'v1';
  const CACHE_TTL = 6 * 60 * 60 * 1000;
  const MAX_SEARCH_PAGES = 2;

  // Longest names are intentionally listed first so "blue sapphire" is not
  // reduced to "blue" + "sapphire" and multi-word stones stay intact.
  const GEMSTONE_NAMES = [
    'black rutilated quartz',
    'london blue topaz',
    'black tourmalinated quartz',
    'tourmalinated quartz',
    'pink tourmaline',
    'green amethyst',
    'blue sapphire',
    'black obsidian',
    'smoky quartz',
    'rose quartz',
    'moss agate',
    'lapis lazuli',
    'black onyx',
    'rainbow moonstone',
    'white sapphire',
    'white topaz',
    'blue topaz',
    'rutilated quartz',
    'alexandrite',
    'labradorite',
    'aquamarine',
    'bloodstone',
    'moonstone',
    'tourmaline',
    'malachite',
    'morganite',
    'turquoise',
    'amazonite',
    'amethyst',
    'obsidian',
    'sapphire',
    'peridot',
    'citrine',
    'garnet',
    'quartz',
    'onyx',
    'topaz',
    'pearl',
    'ruby',
    'opal'
  ];

  const MATERIAL_OPTION_NAMES = ['material', 'metal', 'metal finish', 'finish'];
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const gemstonePattern = new RegExp(
    `(^|[^a-z0-9])(${GEMSTONE_NAMES.map(escapeRegExp).join('|')})(?=$|[^a-z0-9])`,
    'gi'
  );

  function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function canonicalize(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[’‘]/g, "'")
      .replace(/&/g, ' and ')
      .toLowerCase();
  }

  function normalizeTitle(title) {
    return canonicalize(title)
      .replace(gemstonePattern, '$1 ')
      .replace(/\b(18k|18kt|14k|14kt|925)\b/g, ' ')
      .replace(/\b(yellow gold|rose gold|white gold|sterling silver|gold vermeil|vermeil)\b/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function gemstoneLabel(title, gemstoneNames) {
    if (Array.isArray(gemstoneNames)) {
      const names = [...new Set(gemstoneNames
        .filter((name) => typeof name === 'string' && name.trim())
        .map((name) => canonicalize(name).trim()))];
      if (names.length) {
        return names.map((name) => name.replace(/\b\w/g, (letter) => letter.toUpperCase())).join(' & ');
      }
    }
    const normalized = canonicalize(title);
    const matches = [];

    GEMSTONE_NAMES.forEach((name) => {
      const pattern = new RegExp(`(^|[^a-z0-9])(${escapeRegExp(name)})(?=$|[^a-z0-9])`, 'i');
      const match = pattern.exec(normalized);
      if (!match) return;

      const start = match.index + match[1].length;
      const end = start + match[2].length;
      const overlaps = matches.some((item) => start < item.end && end > item.start);

      if (!overlaps) matches.push({ start, end, name: match[2] });
    });

    const result = matches
      .sort((a, b) => a.start - b.start)
      .map((item) => item.name.replace(/\b\w/g, (letter) => letter.toUpperCase()))
      .join(' & ');

    return result || title;
  }

  function normalizeOptionName(option) {
    if (typeof option === 'string') return canonicalize(option).trim();
    return canonicalize(option && option.name).trim();
  }

  function normalizeOptionValue(value) {
    const normalized = canonicalize(value)
      .replace(/\b(18|14)\s*kt\b/g, '$1k')
      .replace(/\s+/g, ' ')
      .trim();

    // Product families do not always use identical wording for the same metal.
    // Keep the visible wording untouched while comparing common aliases safely.
    if (/\b(sterling silver|925 silver|925 sterling|silver)\b/.test(normalized)) return 'silver';
    if (/\brose gold\b/.test(normalized)) return 'rose gold';
    if (/\bwhite gold\b/.test(normalized)) return 'white gold';
    if (/\b(yellow gold|gold vermeil|vermeil|gold)\b/.test(normalized)) return 'yellow gold';
    if (/\bplatinum\b/.test(normalized)) return 'platinum';

    return normalized;
  }

  function materialIndex(product) {
    const options = Array.isArray(product.options) ? product.options : [];

    for (const name of MATERIAL_OPTION_NAMES) {
      const index = options.findIndex((option) => normalizeOptionName(option) === name);
      if (index !== -1) return index;
    }

    return -1;
  }

  function productImage(product, variant) {
    const image = variant && (variant.image || variant.featured_image);

    if (typeof image === 'string' && image) return image;
    if (image && typeof image === 'object') return image.src || image.url || '';

    const featured = product.featuredImage || product.featured_image;
    if (typeof featured === 'string') return featured;
    return featured && (featured.src || featured.url) ? featured.src || featured.url : '';
  }

  function productImageAlt(product, variant) {
    const image = variant && (variant.image || variant.featured_image);
    if (variant && variant.imageAlt) return variant.imageAlt;
    if (image && typeof image === 'object' && image.alt) return image.alt;
    return product.featuredImageAlt || product.title || '';
  }

  function productUrl(product) {
    // Keep crawlable links canonical even when the discovery search returned
    // tracking parameters or a selected variant in product.url.
    return (product.url || `/products/${encodeURIComponent(product.handle || '')}`).split(/[?#]/)[0];
  }

  function variantForMaterial(product, material) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const index = materialIndex(product);
    const wanted = normalizeOptionValue(material);

    if (wanted && index === -1) return null;

    if (index !== -1 && wanted) {
      const availableMatch = variants.find(
        (variant) =>
          variant.available !== false &&
          normalizeOptionValue((variant.options || [])[index]) === wanted
      );
      if (availableMatch) return availableMatch;

      const exactMatch = variants.find(
        (variant) => normalizeOptionValue((variant.options || [])[index]) === wanted
      );
      if (exactMatch) return exactMatch;

      // A product that explicitly lacks the selected Material/Metal should not
      // silently display a different metal colour.
      return null;
    }

    return variants.find((variant) => variant.available !== false) || variants[0] || null;
  }

  function appendVariant(url, variant) {
    if (!variant || !variant.id) return url;

    try {
      const resolved = new URL(url, window.location.origin);
      ['_pos', '_sid', '_ss'].forEach((parameter) => resolved.searchParams.delete(parameter));
      resolved.searchParams.set('variant', variant.id);
      return `${resolved.pathname}${resolved.search}${resolved.hash}`;
    } catch (_error) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}variant=${encodeURIComponent(variant.id)}`;
    }
  }

  function uniqueProducts(products) {
    const seen = new Set();

    return products.filter((product) => {
      const key = String(product.id || product.handle || '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function readSeed(root) {
    const script = root.querySelector('[data-da-gemstone-picker-products]');
    if (!script) return [];

    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed.products) ? parsed.products : [];
    } catch (error) {
      console.warn('[DA gemstone picker] Invalid embedded product data.', error);
      return [];
    }
  }

  function readCache(key) {
    try {
      const raw = window.sessionStorage.getItem(key);
      if (!raw) return null;
      const cached = JSON.parse(raw);
      if (!cached.savedAt || Date.now() - cached.savedAt > CACHE_TTL) {
        window.sessionStorage.removeItem(key);
        return null;
      }
      return Array.isArray(cached.products) ? cached.products : null;
    } catch (_error) {
      return null;
    }
  }

  function writeCache(key, products) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), products }));
    } catch (_error) {
      // Storage can be disabled or full; the picker works without caching.
    }
  }

  async function searchProducts(root, designKey) {
    const cacheKey = `da-gemstone-picker:${CACHE_VERSION}:${designKey}`;
    const cached = readCache(cacheKey);
    if (cached) return cached;

    const searchUrl = root.dataset.searchUrl || '/search';
    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('view', SEARCH_VIEW);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', designKey);
    url.searchParams.set('page', '1');

    const allProducts = [];
    let totalPages = 1;

    for (let page = 1; page <= Math.min(totalPages, MAX_SEARCH_PAGES); page += 1) {
      url.searchParams.set('page', String(page));

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) throw new Error(`Search request failed (${response.status})`);

      const payload = await response.json();
      const pageProducts = Array.isArray(payload.products) ? payload.products : [];
      allProducts.push(...pageProducts);
      totalPages = Math.max(1, Number(payload.pages) || 1);
    }

    const products = uniqueProducts(allProducts);
    writeCache(cacheKey, products);
    return products;
  }

  function selectedVariantId(root) {
    const section = root.closest('.shopify-section') || root.parentElement || document;
    const scopedInput = section.querySelector('form[action*="/cart/add"] [name="id"]');
    if (scopedInput && scopedInput.value) {
      return String(scopedInput.value);
    }

    const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    return input && input.value ? String(input.value) : String(root.dataset.currentVariantId || '');
  }

  class GemstonePicker {
    constructor(root) {
      this.root = root;
      this.trigger = root.querySelector('[data-da-gemstone-picker-open]');
      this.overlay = root.querySelector('[data-da-gemstone-picker-overlay]');
      this.grid = root.querySelector('[data-da-gemstone-picker-grid]');
      this.currentLabel = root.querySelector('[data-da-current-gemstone]');
      this.triggerImage = root.querySelector('.da-gemstone-picker__trigger-image img');
      this.seed = uniqueProducts(readSeed(root));
      this.currentProduct = this.seed.find(
        (product) => String(product.id) === String(root.dataset.currentProductId)
      );
      this.products = this.seed;
      this.currentMaterial = '';
      this.previouslyFocused = null;
      this.scrollY = 0;
      this.renderQueued = false;

      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onDocumentChange = this.onDocumentChange.bind(this);
      this.onVariantEvent = this.onVariantEvent.bind(this);
    }

    init() {
      if (!this.currentProduct || !this.trigger || !this.overlay || !this.grid) return;

      this.bindEvents();
      this.refreshMaterial();

      if (this.root.dataset.manualProducts === 'true') {
        this.finish(this.seed);
        return;
      }

      const designKey = normalizeTitle(this.currentProduct.title);
      if (!designKey) return;

      const load = () => {
        searchProducts(this.root, designKey)
          .then((products) => {
            const currentType = canonicalize(this.currentProduct.type).trim();
            const exactMatches = products.filter((product) => {
              const typeMatches =
                !currentType || canonicalize(product.type).trim() === currentType;
              return typeMatches && normalizeTitle(product.title) === designKey;
            });

            this.finish(uniqueProducts([...this.seed, ...exactMatches]));
          })
          .catch((error) => {
            console.warn('[DA gemstone picker] Automatic matching was unavailable.', error);
            this.finish(this.seed);
          });
      };

      // Start asynchronously as soon as the document is ready; do not wait for unrelated images or apps.
      load();
    }

    bindEvents() {
      this.trigger.addEventListener('click', () => this.open());
      this.overlay.querySelectorAll('[data-da-gemstone-picker-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.grid.addEventListener('click', (event) => {
        const card = event.target.closest('.da-gemstone-picker__card');
        if (!card || !this.grid.contains(card)) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (card.getAttribute('aria-current') === 'page') {
          event.preventDefault();
          this.close();
          return;
        }
        if (!card.matches('a[href]') || card.hasAttribute('download') ||
            (card.target && card.target !== '_self')) return;
        const product = this.products.find((item) => String(item.id) === card.dataset.daProductId);
        if (!product) return;

        // Resolve at activation time, not from the variant captured at page load
        // or when the drawer was opened. Leave href clean for crawlers/new tabs.
        this.refreshMaterial();
        const variant = this.currentMaterial
          ? variantForMaterial(product, this.currentMaterial)
          : null;
        event.preventDefault();
        window.location.assign(appendVariant(productUrl(product), variant));
      });

      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('change', this.onDocumentChange);

      ['variant:change', 'product:variant-change', 'variant-change'].forEach((eventName) => {
        document.addEventListener(eventName, this.onVariantEvent);
      });
    }

    finish(products) {
      this.products = uniqueProducts(products);

      if (this.products.length < 2) {
        this.root.hidden = true;
        return;
      }

      this.root.hidden = this.render() < 2;
    }

    refreshMaterial(variantFromEvent) {
      const variants = this.currentProduct.variants || [];
      const variantId = variantFromEvent && variantFromEvent.id
        ? String(variantFromEvent.id)
        : selectedVariantId(this.root);
      const variant =
        variants.find((item) => String(item.id) === variantId) ||
        variants.find((item) => String(item.id) === String(this.root.dataset.currentVariantId)) ||
        variants[0];
      const index = materialIndex(this.currentProduct);

      this.currentMaterial = variant && index !== -1 ? (variant.options || [])[index] || '' : '';

      this.root.dataset.currentVariantId = variant && variant.id ? variant.id : variantId;
    }

    queueRender(variant) {
      this.refreshMaterial(variant);
      if (this.renderQueued) return;

      this.renderQueued = true;
      window.requestAnimationFrame(() => {
        this.renderQueued = false;
        if (this.products.length > 1) this.root.hidden = this.render() < 2;
      });
    }

    onDocumentChange(event) {
      if (!event.target.closest('form[action*="/cart/add"], product-form, variant-selects, variant-radios')) {
        return;
      }
      window.setTimeout(() => this.queueRender(), 0);
    }

    onVariantEvent(event) {
      const detail = event.detail || {};
      this.queueRender(detail.variant || detail.productVariant || null);
    }

    render() {
      const currentId = String(this.currentProduct.id);
      const currentVariant = variantForMaterial(this.currentProduct, this.currentMaterial);
      const label = gemstoneLabel(this.currentProduct.title, this.currentProduct.gemstones);
      this.currentLabel.textContent = label;

      if (this.triggerImage) {
        const source = productImage(this.currentProduct, currentVariant);
        if (source) {
          // Liquid's initial image has a srcset. Remove it before changing src,
          // otherwise the browser can keep displaying the previous material.
          this.triggerImage.removeAttribute('srcset');
          this.triggerImage.removeAttribute('sizes');
          this.triggerImage.src = source.replace(/([?&])width=\d+/, '$1width=120');
        }
        // The adjacent gemstone text already names this control; the thumbnail
        // is decorative here and would otherwise be announced twice.
        this.triggerImage.alt = '';
      }

      if (this.root.dataset.manualProducts === 'true') {
        // Liquid owns these anchors. Preserve the nodes, hrefs and ordering;
        // only refresh the material thumbnail and concise gemstone label.
        const productsById = new Map(this.products.map((product) => [String(product.id), product]));
        const cards = this.grid.querySelectorAll('.da-gemstone-picker__card[data-da-product-id]');
        cards.forEach((card) => {
          const product = productsById.get(card.dataset.daProductId);
          if (!product) return;
          const variant = variantForMaterial(product, this.currentMaterial);
          const image = card.querySelector('.da-gemstone-picker__card-image img');
          const name = card.querySelector('.da-gemstone-picker__card-name');
          if (image) {
            const source = productImage(product, variant);
            if (source) {
              image.removeAttribute('srcset');
              image.removeAttribute('sizes');
              image.src = source.replace(/([?&])width=\d+/, '$1width=360');
            }
            image.alt = productImageAlt(product, variant);
          }
          if (name) name.textContent = gemstoneLabel(product.title, product.gemstones);
        });
        return cards.length;
      }

      const fragment = document.createDocumentFragment();
      const productsForMaterial = this.products
        .map((product) => ({ product, variant: variantForMaterial(product, this.currentMaterial) }));

      productsForMaterial.forEach(({ product, variant }) => {
        const card = document.createElement(String(product.id) === currentId ? 'button' : 'a');
        const imageWrap = document.createElement('span');
        const name = document.createElement('span');
        const imageSource = productImage(product, variant);

        card.className = 'da-gemstone-picker__card';
        card.dataset.daProductId = String(product.id);
        if (String(product.id) === currentId) {
          card.type = 'button';
          card.setAttribute('aria-current', 'page');
        } else {
          card.href = productUrl(product);
        }

        imageWrap.className = 'da-gemstone-picker__card-image';
        if (imageSource) {
          const image = document.createElement('img');
          image.src = imageSource.replace(/([?&])width=\d+/, '$1width=360');
          image.alt = productImageAlt(product, variant);
          image.loading = 'lazy';
          image.decoding = 'async';
          imageWrap.appendChild(image);
        }

        name.className = 'da-gemstone-picker__card-name';
        name.textContent = gemstoneLabel(product.title, product.gemstones);

        card.append(imageWrap, name);
        fragment.appendChild(card);
      });

      this.grid.replaceChildren(fragment);
      return productsForMaterial.length;
    }

    open() {
      if (this.products.length < 2) return;

      this.refreshMaterial();
      this.render();

      this.previouslyFocused = document.activeElement;
      this.scrollY = window.scrollY;
      this.overlay.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      document.body.style.setProperty('--da-picker-scroll-offset', `${-this.scrollY}px`);
      document.documentElement.classList.add('da-gemstone-picker-is-open');
      document.body.classList.add('da-gemstone-picker-is-open');

      const closeButton = this.overlay.querySelector('.da-gemstone-picker__close');
      window.requestAnimationFrame(() => closeButton && closeButton.focus({ preventScroll: true }));
    }

    close() {
      if (this.overlay.hidden) return;

      const scrollStyle = document.documentElement.style;
      const previousBehavior = scrollStyle.getPropertyValue('scroll-behavior');
      const previousPriority = scrollStyle.getPropertyPriority('scroll-behavior');
      scrollStyle.setProperty('scroll-behavior', 'auto', 'important');
      this.overlay.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('da-gemstone-picker-is-open');
      document.body.classList.remove('da-gemstone-picker-is-open');
      document.body.style.removeProperty('--da-picker-scroll-offset');
      window.scrollTo({ top: this.scrollY, left: 0, behavior: 'instant' });

      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus({ preventScroll: true });
      }
      if (previousBehavior) {
        scrollStyle.setProperty('scroll-behavior', previousBehavior, previousPriority);
      } else {
        scrollStyle.removeProperty('scroll-behavior');
      }
    }

    onDocumentKeydown(event) {
      if (this.overlay.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(this.overlay.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => element.offsetParent !== null
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!this.overlay.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  // Reuse the gemstone drawer, but let the theme's native radio inputs remain
  // the source of truth for prices, size, inventory, media and add-to-cart.
  class MaterialPicker extends GemstonePicker {
    constructor(root, fieldset) {
      super(root);
      this.fieldset = fieldset;
      this.inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    }

    init() {
      this.bindEvents();
      this.grid.addEventListener('click', (event) => {
        const card = event.target.closest('[data-da-material-value]');
        if (!card || !this.grid.contains(card) || card.disabled) return;
        const input = this.inputs.find((item) => item.value === card.dataset.daMaterialValue);
        if (!input || input.disabled) return;
        this.close();
        if (!input.checked) input.click();
        this.queueRender();
      });
      this.queueRender();
    }

    queueRender() {
      const checked = this.inputs.find((input) => input.checked);
      this.currentMaterial = checked ? checked.value : this.inputs[0].value;
      this.render();
    }

    render() {
      const variant = variantForMaterial(this.currentProduct, this.currentMaterial);
      this.currentLabel.textContent = this.currentMaterial;
      if (this.triggerImage) {
        const source = productImage(this.currentProduct, variant);
        this.triggerImage.removeAttribute('srcset');
        this.triggerImage.removeAttribute('sizes');
        if (source) this.triggerImage.src = source.replace(/([?&])width=\d+/, '$1width=120');
        this.triggerImage.alt = '';
      }
      const fragment = document.createDocumentFragment();
      this.inputs.forEach((input) => {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'da-gemstone-picker__card';
        card.dataset.daMaterialValue = input.value;
        card.disabled = input.disabled;
        card.setAttribute('aria-pressed', String(input.checked));
        if (input.checked) card.setAttribute('aria-current', 'page');
        const imageWrap = document.createElement('span');
        imageWrap.className = 'da-gemstone-picker__card-image';
        const choice = variantForMaterial(this.currentProduct, input.value);
        const source = productImage(this.currentProduct, choice);
        if (source) {
          const image = document.createElement('img');
          image.src = source.replace(/([?&])width=\d+/, '$1width=360');
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          imageWrap.appendChild(image);
        }
        const name = document.createElement('span');
        name.className = 'da-gemstone-picker__card-name';
        name.textContent = input.value;
        card.append(imageWrap, name);
        fragment.appendChild(card);
      });
      this.grid.replaceChildren(fragment);
      return this.inputs.length;
    }

    open() {
      this.queueRender();
      // The shared drawer checks the number of options before opening.
      this.products = this.inputs;
      super.open();
    }
  }

  function initializeMaterial(gemstoneRoot) {
    const section = gemstoneRoot.closest('.product-detail--main');
    if (!section || section.querySelector('[data-da-material-picker]')) return;
    const product = readSeed(gemstoneRoot).find(
      (item) => String(item.id) === gemstoneRoot.dataset.currentProductId
    );
    if (!product) return;
    const index = materialIndex(product);
    if (index < 0) return;
    const selector = section.querySelector('variant-selects:not([data-sticky="true"])');
    if (!selector) return;
    const fieldset = Array.from(selector.querySelectorAll('fieldset'))[index];
    if (!fieldset || fieldset.querySelectorAll('input[type="radio"]').length < 2) return;

    const root = gemstoneRoot.cloneNode(true);
    root.removeAttribute('data-da-gemstone-picker');
    root.removeAttribute('data-da-gemstone-picker-initialized');
    root.setAttribute('data-da-material-picker', '');
    root.hidden = false;
    root.querySelector('.da-gemstone-picker__step').textContent = 'Choose Material';
    root.querySelector('.da-gemstone-picker__trigger-label > span').textContent = 'Material:';
    const overlay = root.querySelector('[data-da-gemstone-picker-overlay]');
    const dialog = overlay.querySelector('[role="dialog"]');
    const heading = dialog.querySelector('h2');
    overlay.id = `da-material-picker-dialog-${product.id}`;
    heading.id = `da-material-picker-title-${product.id}`;
    heading.textContent = 'Material';
    dialog.setAttribute('aria-labelledby', heading.id);
    root.querySelector('[data-da-gemstone-picker-open]').setAttribute('aria-controls', overlay.id);
    overlay.querySelectorAll('[data-da-gemstone-picker-close]').forEach((button) => {
      button.setAttribute('aria-label', 'Close material selector');
    });
    // Keep the native inputs intact; hide them only after enhancement succeeds.
    // Keep both custom pickers outside variant-selects: the theme reads its
    // first JSON script as the variant array and must not see our product seed.
    selector.before(root);
    new MaterialPicker(root, fieldset).init();
    fieldset.style.display = 'none';
    // Match the option order: material, gemstone, then the existing size controls.
    root.after(gemstoneRoot);
  }

  function initialize(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.daGemstonePickerInitialized === 'true') return;
      root.dataset.daGemstonePickerInitialized = 'true';
      initializeMaterial(root);
      new GemstonePicker(root).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
